import React, { useState, useEffect } from 'react';
import { Mail, Phone, Send, Shield } from 'lucide-react';
import { Amplify } from 'aws-amplify';
import ReactGA from 'react-ga4';
import { signUp, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import DataExtractionAnimation from '../components/DataExtractionAnimation';
import awsConfig from '../aws-config';

// Configure Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: awsConfig.Auth.userPoolId,
      userPoolClientId: awsConfig.Auth.userPoolWebClientId,
      loginWith: {
        email: true,
      },
    }
  }
});

// Lambda Function URL for form submission
const SUBMIT_FORM_URL = 'https://jrf3snoyjkk3vl62u6jnmvgggy0xnxmx.lambda-url.us-east-1.on.aws/';

const Contact = () => {
  const [otpCode, setOtpCode] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    company: '',
    phone: '',
    product: 'General Inquiry',
    message: ''
  });

  // Current step: 'form', 'otp', 'success'
  const [currentStep, setCurrentStep] = useState('form');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // Timer for resend OTP cooldown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Step 1: Submit form and send OTP
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate form fields
    if (!formData.email || !formData.name || !formData.message) {
      setError('Please fill in all required fields (Email, Name, and Message).');
      setLoading(false);
      return;
    }

    try {
      // Generate a random password (Cognito requires it)
      const tempPassword = `TempPass${Math.random().toString(36).slice(-8)}!1Aa`;

      // Sign up with email to get OTP
      await signUp({
        username: formData.email,
        password: tempPassword,
        options: {
          userAttributes: {
            email: formData.email,
            name: formData.name,
          }
        }
      });

      setCurrentStep('otp');
      setResendTimer(60);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      // Handle network errors with user-friendly message
      if (err.message === 'Load failed' || err.message?.includes('network') || err.name === 'NetworkError') {
        setError('Network connection error. Please check your internet connection and try again.');
      } else {
        setError(err.message || 'Failed to send verification code. Please try again.');
      }
      console.error('Error sending OTP:', err);
    }
  };

  // Step 2: Verify OTP and submit to Lambda
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Confirm the user with the OTP code
      await confirmSignUp({
        username: formData.email,
        confirmationCode: otpCode
      });

      // After OTP verified, submit form to Lambda
      const response = await fetch(SUBMIT_FORM_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name,
          company: formData.company,
          phone: formData.phone,
          product: formData.product,
          message: formData.message,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to submit form');
      }

      // Track successful contact form submission as a conversion in GA4
      ReactGA.event({
        category: 'Lead Generation',
        action: 'Contact Form Submitted',
        label: formData.product,
        value: 1,
      });

      setCurrentStep('success');
      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err.name === 'CodeMismatchException') {
        setError('Invalid OTP code. Please check and try again.');
      } else if (err.name === 'ExpiredCodeException') {
        setError('OTP code has expired. Please request a new one.');
      } else if (err.message === 'Load failed' || err.message?.includes('network') || err.name === 'NetworkError') {
        setError('Network connection error. Please check your internet connection and try again.');
      } else {
        setError(err.message || 'Verification failed. Please try again.');
      }
      console.error('Error verifying OTP:', err);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendTimer > 0) return;

    setLoading(true);
    setError('');

    try {
      await resendSignUpCode({ username: formData.email });
      setResendTimer(60);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      // Handle network errors with user-friendly message
      if (err.message === 'Load failed' || err.message?.includes('network') || err.name === 'NetworkError') {
        setError('Network connection error. Please check your internet connection and try again.');
      } else {
        setError(err.message || 'Failed to resend OTP. Please try again.');
      }
      console.error('Error resending OTP:', err);
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  // Reset form
  const handleReset = () => {
    setOtpCode('');
    setFormData({
      email: '',
      name: '',
      company: '',
      phone: '',
      product: 'General Inquiry',
      message: ''
    });
    setCurrentStep('form');
    setError('');
  };

  const contactRef = useScrollAnimation({ threshold: 0.2 });

  return (
    <div className="pt-20">
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20 animate-slide-down relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 animate-slide-up">Get in Touch</h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto animate-slide-up" style={{ animationDelay: '0.1s' }}>
            Ready to transform your business? Our team is here to help.
          </p>
        </div>
      </section>

      <section className="py-24 bg-white" ref={contactRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-start">

            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className={`text-2xl font-bold text-gray-900 mb-8 ${contactRef.isVisible ? 'animate-slide-up' : 'opacity-0'}`}>Contact Information</h2>
              <div className="space-y-8">
                <div className={`flex items-start gap-4 group hover:translate-x-2 transition-transform ${contactRef.isVisible ? 'animate-stagger-1' : 'opacity-0'}`}>
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center text-cyan-600 flex-shrink-0 transition-all duration-300 md-animate-float group-hover:scale-110 md:group-hover:scale-125 group-hover:bg-cyan-200 group-hover:shadow-lg group-hover:shadow-cyan-300/50">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email Us</h3>
                    <p className="text-gray-600">contact@ordinaa.com</p>
                  </div>
                </div>

                <div className={`flex items-start gap-4 group hover:translate-x-2 transition-transform ${contactRef.isVisible ? 'animate-stagger-2' : 'opacity-0'}`}>
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center text-cyan-600 flex-shrink-0 transition-all duration-300 md-animate-float group-hover:scale-110 md:group-hover:scale-125 group-hover:bg-cyan-200 group-hover:shadow-lg group-hover:shadow-cyan-300/50" style={{ animationDelay: '0.2s' }}>
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Call Us</h3>
                    <p className="text-gray-600">+91 9380470079</p>
                    </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className={`lg:col-span-2 bg-white rounded-3xl shadow-xl p-8 border border-gray-100 ${contactRef.isVisible ? 'animate-scale-in' : 'opacity-0'}`}>
              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Step 1: Contact Form */}
              {currentStep === 'form' && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Send className="text-cyan-600" size={28} />
                    <h2 className="text-2xl font-bold text-gray-900">Send us a Message</h2>
                  </div>
                  <p className="text-sm text-gray-600 mb-6">
                    Fill out the form below and we'll send a verification code to your email before submitting.
                  </p>

                  <form onSubmit={handleSubmitForm} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={loading}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all disabled:bg-gray-100 hover:border-cyan-300 disabled:hover:border-gray-300 ripple"
                        placeholder="your.email@company.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        disabled={loading}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all disabled:bg-gray-100 hover:border-cyan-300 disabled:hover:border-gray-300 ripple"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        disabled={loading}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all disabled:bg-gray-100"
                        placeholder="Company Ltd."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={loading}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all disabled:bg-gray-100"
                        placeholder="+91 9380470079"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Product Interest</label>
                      <select
                        name="product"
                        value={formData.product}
                        onChange={handleChange}
                        disabled={loading}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all bg-white disabled:bg-gray-100"
                      >
                        <option>General Inquiry</option>
                        <option>LendSmart AI</option>
                        <option>Niyukti AI</option>
                        <option>PQC Migration</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                      <textarea
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        disabled={loading}
                        rows="4"
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all resize-none disabled:bg-gray-100"
                        placeholder="How can we help you?"
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-4 rounded-xl font-bold hover:shadow-lg hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading ? 'Processing...' : (
                        <>
                          <Shield className="w-8 h-8 md:w-5 md:h-5 flex-shrink-0 ml-2 md:ml-0" />
                          Send Message (Email Verification Required)
                        </>
                      )}
                    </button>
                  </form>

                  <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-sm text-blue-700">
                      <strong><Shield size={14} className="inline" /> Secure Submission:</strong> After clicking send, we'll verify your email with a code to ensure authenticity.
                    </p>
                  </div>
                </div>
              )}

              {/* Step 2: OTP Verification */}
              {currentStep === 'otp' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-cyan-100 rounded-full flex items-center justify-center text-cyan-600 mx-auto mb-4">
                      <Mail size={40} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h2>
                    <p className="text-gray-600">
                      We've sent a 6-digit verification code to<br />
                      <span className="font-semibold text-gray-900">{formData.email}</span>
                    </p>
                  </div>

                  <form onSubmit={handleVerifyOTP} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Verification Code</label>
                      <input
                        type="text"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value)}
                        disabled={loading}
                        maxLength="6"
                        pattern="[0-9]{6}"
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all text-center text-2xl tracking-widest font-mono disabled:bg-gray-100"
                        placeholder="000000"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading || otpCode.length !== 6}
                      className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-4 rounded-xl font-bold hover:shadow-lg hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Verifying & Submitting...' : 'Verify & Send Message'}
                    </button>
                  </form>

                  <div className="text-center space-y-3">
                    <button
                      onClick={handleResendOTP}
                      disabled={resendTimer > 0 || loading}
                      className="text-sm text-cyan-600 font-semibold hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                    >
                      {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend verification code'}
                    </button>
                    <br />
                    <button
                      onClick={handleReset}
                      disabled={loading}
                      className="text-sm text-gray-600 hover:underline disabled:cursor-not-allowed"
                    >
                      Change email address
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Success */}
              {currentStep === 'success' && (
                <div className="h-full flex flex-col items-center justify-center text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-6">
                    <Send size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent Successfully!</h3>
                  <p className="text-gray-600 mb-2">Thank you for reaching out, {formData.name}.</p>
                  <p className="text-gray-600 mb-6">Our team will get back to you within 24 hours at {formData.email}.</p>
                  <button
                    onClick={handleReset}
                    className="mt-4 px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all"
                  >
                    Send Another Message
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
